/**
 * The "Terminales" panel — full CRUD for the terminals nested under one location.
 *
 * A terminal's `yubikey` field must name a real pool entry (`apps/api/src/locations/service.ts`'s
 * `requirePooledYubikey`), and the API returns a `validation_failed` naming the `yubikey` field
 * when it does not — surfaced here the same way `LocationEditForm` surfaces a field error, rather
 * than as a generic failure banner.
 */
import type { CreateMaquina, Maquina, UpdateMaquina } from '@apuestapr/contracts';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  icons,
  toast,
} from '@apuestapr/ui';
import { useState, type FormEvent, type ReactElement } from 'react';

import type { ManagedRowList } from './hooks.ts';

interface Draft {
  readonly yubikey: string;
  readonly yubikeyNumber: string;
  readonly modelo: string;
  readonly nombre: string;
  readonly notas: string;
}

const emptyDraft: Draft = { yubikey: '', yubikeyNumber: '', modelo: '', nombre: '', notas: '' };

const draftToCreate = (draft: Draft): CreateMaquina => ({
  yubikey: draft.yubikey,
  yubikeyNumber: draft.yubikeyNumber,
  modelo: draft.modelo,
  ...(draft.nombre.trim() === '' ? {} : { nombre: draft.nombre }),
  ...(draft.notas.trim() === '' ? {} : { notas: draft.notas }),
});

interface MaquinaRowProps {
  readonly maquina: Maquina;
  readonly busy: boolean;
  readonly onUpdate: (id: number, patch: Partial<Draft>) => void;
  readonly onDelete: (id: number) => void;
  readonly fieldError: string | undefined;
}

const MaquinaRow = ({
  maquina,
  busy,
  onUpdate,
  onDelete,
  fieldError,
}: MaquinaRowProps): ReactElement => {
  const [nombre, setNombre] = useState(maquina.nombre ?? '');
  const [yubikey, setYubikey] = useState(maquina.yubikey);

  return (
    <li className="grid gap-3 rounded-md border border-border p-3 sm:grid-cols-[1fr_1fr_auto]">
      <Field label="Nombre" controlId={`maquina-${String(maquina.id)}-nombre`}>
        {(control) => (
          <Input
            {...control}
            disabled={busy}
            value={nombre}
            onChange={(event) => setNombre(event.currentTarget.value)}
            onBlur={() => onUpdate(maquina.id, { nombre })}
          />
        )}
      </Field>
      <Field label="YubiKey" controlId={`maquina-${String(maquina.id)}-yubikey`} error={fieldError}>
        {(control) => (
          <Input
            {...control}
            disabled={busy}
            value={yubikey}
            onChange={(event) => setYubikey(event.currentTarget.value)}
            onBlur={() => onUpdate(maquina.id, { yubikey })}
          />
        )}
      </Field>
      <div className="flex items-end">
        <Button
          disabled={busy}
          onClick={() => onDelete(maquina.id)}
          size="sm"
          variant="destructive"
        >
          Eliminar <span className="sr-only">{maquina.modelo}</span>
        </Button>
      </div>
      <p className="col-span-full m-0 text-sm text-muted-foreground">
        Modelo: {maquina.modelo} · Número de YubiKey: {maquina.yubikeyNumber} · Estado:{' '}
        {maquina.estado}
      </p>
    </li>
  );
};

const CreateMaquinaForm = ({
  busy,
  fieldError,
  onCreate,
}: {
  readonly busy: boolean;
  readonly fieldError: string | undefined;
  readonly onCreate: (draft: Draft) => void;
}): ReactElement => {
  const [draft, setDraft] = useState<Draft>(emptyDraft);

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (
      draft.yubikey.trim() === '' ||
      draft.yubikeyNumber.trim() === '' ||
      draft.modelo.trim() === ''
    ) {
      return;
    }
    onCreate(draft);
    setDraft(emptyDraft);
  };

  return (
    <form
      className="grid gap-3 rounded-md border border-dashed border-border p-3 sm:grid-cols-4"
      noValidate
      onSubmit={submit}
    >
      <Field
        label="YubiKey de la nueva terminal"
        controlId="new-maquina-yubikey"
        error={fieldError}
        required
      >
        {(control) => (
          <Input
            {...control}
            disabled={busy}
            value={draft.yubikey}
            onChange={(event) => {
              const { value } = event.currentTarget;
              setDraft((current) => ({ ...current, yubikey: value }));
            }}
          />
        )}
      </Field>
      <Field label="Número de YubiKey" controlId="new-maquina-yubikey-number" required>
        {(control) => (
          <Input
            {...control}
            disabled={busy}
            value={draft.yubikeyNumber}
            onChange={(event) => {
              const { value } = event.currentTarget;
              setDraft((current) => ({ ...current, yubikeyNumber: value }));
            }}
          />
        )}
      </Field>
      <Field label="Modelo" controlId="new-maquina-modelo" required>
        {(control) => (
          <Input
            {...control}
            disabled={busy}
            value={draft.modelo}
            onChange={(event) => {
              const { value } = event.currentTarget;
              setDraft((current) => ({ ...current, modelo: value }));
            }}
          />
        )}
      </Field>
      <Field label="Nombre de la nueva terminal (opcional)" controlId="new-maquina-nombre">
        {(control) => (
          <Input
            {...control}
            disabled={busy}
            value={draft.nombre}
            onChange={(event) => {
              const { value } = event.currentTarget;
              setDraft((current) => ({ ...current, nombre: value }));
            }}
          />
        )}
      </Field>
      <Field label="Notas de la nueva terminal (opcional)" controlId="new-maquina-notas">
        {(control) => (
          <Input
            {...control}
            disabled={busy}
            value={draft.notas}
            onChange={(event) => {
              const { value } = event.currentTarget;
              setDraft((current) => ({ ...current, notas: value }));
            }}
          />
        )}
      </Field>
      <div className="flex items-end">
        <Button disabled={busy} type="submit">
          Añadir terminal
        </Button>
      </div>
    </form>
  );
};

export interface MaquinasPanelProps {
  readonly managed: ManagedRowList<Maquina, CreateMaquina, UpdateMaquina>;
}

export const MaquinasPanel = ({ managed }: MaquinasPanelProps): ReactElement => {
  const busy = managed.mutation.status === 'loading';
  const fieldError =
    managed.mutation.status === 'error' && managed.mutation.failure.kind === 'http'
      ? managed.mutation.failure.details?.find((error) => error.path === 'yubikey')?.message
      : undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle level={3}>Terminales</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {managed.state.status === 'loading' ? (
          <p role="status">Cargando terminales…</p>
        ) : managed.state.status === 'error' ? (
          <p role="alert">No se pudieron cargar las terminales.</p>
        ) : managed.state.data.length === 0 ? (
          <EmptyState
            icon={icons.layers}
            title="Sin terminales"
            description="Añade la primera terminal de esta localidad."
          />
        ) : (
          <ul className="m-0 grid list-none gap-3 p-0">
            {managed.state.data.map((maquina) => (
              <MaquinaRow
                key={maquina.id}
                maquina={maquina}
                busy={busy}
                fieldError={fieldError}
                onUpdate={(id, patch) => {
                  void managed.update(id, patch).then((result) => {
                    if (result.ok) toast({ title: 'Terminal actualizada' });
                  });
                }}
                onDelete={(id) => {
                  void managed.remove(id).then((result) => {
                    if (result.ok) toast({ title: 'Terminal eliminada', variant: 'destructive' });
                  });
                }}
              />
            ))}
          </ul>
        )}

        <CreateMaquinaForm
          busy={busy}
          fieldError={fieldError}
          onCreate={(draft) => {
            void managed.create(draftToCreate(draft)).then((result) => {
              if (result.ok) toast({ title: 'Terminal creada' });
            });
          }}
        />
      </CardContent>
    </Card>
  );
};
